import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { processFieldsToQueryBuilderFormat, validator } from './query-utils/utilsIndex';
import { CONTROL_CLASSNAMES, CONTROL_ELEMENTS, defaultQuery, formatQueryToJson, QUERY_BUILDER_TRANSLATIONS } from './EnvQueryConfig';
import { QueryBuilder } from 'react-querybuilder';
import CollapseButton from '../../common/components/collapse-button/CollapseButton';
import 'react-querybuilder/dist/query-builder.css';

const EnvQueryBuilderView = ({ fields = [], fieldValues = {}, fieldMetadata = [], value = null, onChange = () => {}, onValidityChange= () => {}, showGetDataButton = true, initiallyCollapsed = false }) => {
  const [localQuery, setLocalQuery] = useState(defaultQuery);
  const [visible, setVisible] = useState(!initiallyCollapsed);
  // Increment to force QueryBuilder remount when an external preset/clear changes the query
  const [presetVersion, setPresetVersion] = useState(0);
  const requestIdRef = useRef(0);
  const synchronizingQueryRef = useRef(
    Boolean(value?.query?.rules?.length || value?.filterJson?.rules?.length)
  );
  const previousPresetVersionRef = useRef(presetVersion);
  
  // Track the last filter JSON we sent to prevent treating our own updates as external
  const lastSentFilterRef = useRef(null);
  
  // Track last external filter content to detect genuine external changes
  const lastExternalFilterRef = useRef(null);
  
  // Holds the query used to (re-)initialize QueryBuilder via defaultQuery prop
  const defaultQueryForMountRef = useRef(defaultQuery);
  
  // Keep callback refs in sync so effects/handlers never use stale closures
  const onChangeRef = useRef(onChange);
  const onValidityChangeRef = useRef(onValidityChange);
  onChangeRef.current = onChange;
  onValidityChangeRef.current = onValidityChange;
  
  // (react2angular) may not change the value reference when properties change for proper dependency tracking)
  const valueFilterJson = value?.filterJson;
  const valueQuery = value?.query;

  const queryBuilderFields = useMemo(() => {
    return processFieldsToQueryBuilderFormat(fields, fieldValues, fieldMetadata);
  }, [fields, fieldValues, fieldMetadata]);
    
  // Memoize context object to prevent unnecessary re-renders
  const queryBuilderContext = useMemo(() => ({ fields: queryBuilderFields }), [queryBuilderFields]);

  // Stable content-based strings for comparison (react2angular may pass new object
  // references with the same content on each digest cycle, which breaks === checks)
  const valueFilterString = useMemo(() => {
    return valueFilterJson !== null && valueFilterJson !== undefined ? JSON.stringify(valueFilterJson) : null;
  }, [valueFilterJson]);

  const valueQueryString = useMemo(() => {
    return valueQuery !== null && valueQuery !== undefined ? JSON.stringify(valueQuery) : null;
  }, [valueQuery]);

  const initialQuery = useMemo(() => {
    // If we have a query object with rules, use it directly
    if (valueQuery?.rules?.length > 0) {
      return valueQuery;
    }
    return defaultQuery;
  }, [valueQueryString]);

  useEffect(() => {
    // Only react when the external filter content genuinely changed
    if (valueFilterString !== lastExternalFilterRef.current) {
      lastExternalFilterRef.current = valueFilterString;
      
      // Skip remount only if the parent is echoing back the value we just sent via Get Data.
      // An echo-back requires lastSentFilterRef to be non-null (we actually sent something).
      const isEchoBack = lastSentFilterRef.current !== null && valueFilterString === lastSentFilterRef.current;
      lastSentFilterRef.current = null;
      
      if (!isEchoBack) {
        synchronizingQueryRef.current = true;
        defaultQueryForMountRef.current = initialQuery;
        setLocalQuery(initialQuery);
        setPresetVersion((v) => v + 1);
      }
    }
  }, [initialQuery, valueFilterString]);

  useEffect(() => {
    if (presetVersion !== previousPresetVersionRef.current) {
      previousPresetVersionRef.current = presetVersion;
      synchronizingQueryRef.current = false;
    }
  }, [presetVersion]);

  const handleQueryChange = useCallback((newQuery) => {
    if (synchronizingQueryRef.current) {
      return;
    }
    setLocalQuery(newQuery);
    if (!showGetDataButton) {
      const queryJson = formatQueryToJson(newQuery, queryBuilderFields);
      const queryObject = queryJson ? JSON.parse(queryJson) : null;
      lastSentFilterRef.current = queryObject ? JSON.stringify(queryObject) : null;
      requestIdRef.current += 1;
      onChangeRef.current({
        query: newQuery,
        filterJson: queryObject,
        requestId: requestIdRef.current,
      });
    }
  }, [queryBuilderFields, showGetDataButton]);

  const handleCollapseToggle = useCallback((event) => {
    event.preventDefault();
    setVisible((prev) => !prev);
  }, []);
  
  const handleGetData = useCallback(() => {
    const queryJson = formatQueryToJson(localQuery, queryBuilderFields);
    // Parse JSON string to object to prevent double-stringification in HTTP request
    const queryObject = queryJson ? JSON.parse(queryJson) : null;
    // Track what we're sending (as JSON string for content-based comparison)
    lastSentFilterRef.current = queryObject ? JSON.stringify(queryObject) : null;
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;
    onChangeRef.current({
      query: localQuery,
      filterJson: queryObject,
      requestId: currentRequestId
    });
  }, [localQuery, queryBuilderFields]);
  
  //disable Get Data button if query is invalid
  const isQueryValid = useMemo(() => {
    const checkRules = (query) => {
      if (!query?.rules?.length) return false;
      return query.rules.every((r) => {
        if (r.rules) return checkRules(r); // nested group
        return validator(r) === true;
      });
    };
    return checkRules(localQuery);
  }, [localQuery]);

  // Notify parent of validity changes
  useEffect(() => {
    onValidityChangeRef.current(isQueryValid);
  }, [isQueryValid]);

  const rulesCount = useMemo(() => localQuery?.rules?.length || 0, [localQuery]);
  const collapseButtonLabel = useMemo(() => {
    if (rulesCount > 0) {
      return `Advanced Filters [${rulesCount} Selected]`;
    }
    return 'Advanced Filters';
  }, [rulesCount]);
  
  return (
    <div className='env-query-builder-main-container' data-testid="env-query-builder-container">
      <CollapseButton
        isCollapsed={!visible}
        onToggle={handleCollapseToggle}
        expandedLabel={`Hide ${collapseButtonLabel}`}
        collapsedLabel={`Show ${collapseButtonLabel}`}
        testID="env-query-builder-collapse-button"
      />
      <div 
        className={`query-builder-view__content${visible ? '' : ' query-builder-view__content--collapsed'}`} 
        data-testid="env-query-builder-content">
          <QueryBuilder
            key={presetVersion}
            fields={queryBuilderFields}
            defaultQuery={defaultQueryForMountRef.current}
            onQueryChange={handleQueryChange}
            showCombinatorsBetweenRules={true}
            showNotToggle={true}
            resetOnFieldChange={true}
            resetOnOperatorChange={false}
            autoSelectField={true}
            autoSelectOperator={true}
            addRuleToNewGroups={true}
            showCloneButtons={false}
            enableDragAndDrop={false}
            listsAsArrays={true}
            controlClassnames={CONTROL_CLASSNAMES}
            controlElements={CONTROL_ELEMENTS}
            translations={QUERY_BUILDER_TRANSLATIONS}
            validator={validator}
            context={queryBuilderContext}
            data-testid="env-query-builder"
          />
          {showGetDataButton && (
            <div className="get-data-btn-container">
              <button 
                className="btn btn-primary get-data-btn"
                onClick={handleGetData} 
                disabled={rulesCount > 0 && !isQueryValid} 
                data-testid="env-query-builder-get-data-button">
                  Get Data
              </button>
            </div>
          )}
        </div>
    </div>
  );
};

EnvQueryBuilderView.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.string),
  fieldValues: PropTypes.object,
  fieldMetadata: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    displayName: PropTypes.string,
    variableType: PropTypes.string,
    bitSize: PropTypes.number,
    offset: PropTypes.number,
  })),
  value: PropTypes.object,
  onChange: PropTypes.func,
  onValidityChange: PropTypes.func,
  showGetDataButton: PropTypes.bool,
  initiallyCollapsed: PropTypes.bool,
};

export default EnvQueryBuilderView;
